import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { toggleLoader } from "../../shared/actions/setting";
import FormHandler from "../../shared/utils/FormHandler";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { createUser, getReferencesForRole, updateUser, getMetaData } from "../../utils/mainServices";
import { validateFormUser } from "../../utils/validation";
import { USER_TYPES } from "../../utils/Enum";
import { FormControlLabel, Switch } from "@mui/material"
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';

const UserForm = ({ selectedUserData, onClose, formType, allRoles }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [formErr, setFormErr] = useState("");
  const [showText, setShowText] = useState(false);
  const [roles, setRoles] = useState([]);
  const [references, setReferences] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [localFormType, setLocalFormType] = useState(formType);
  const [isAdvanceOption, setIsAdvanceOption] = useState(false);
  const [isTempUserEnable, setIsTempUserEnable] = useState(false)
  const [selectedDate, setSelectedDate] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState({
    email: false,
    mobile: false,
  });

  const [verificationPopup, setVerificationPopup] = useState({
    isOpen: false,
    fieldName: "",
    fieldValue: "",
  });

   const toggleVerification = (fieldName) => {
    if (localFormType !== "edit") {
      return;
    }
    const fieldValue = fieldName === 'email' ? values?.email : values?.mobile;
    setVerificationPopup({
      isOpen: true,
      fieldName,
      fieldValue,
    });
  };

   const handleVerification = (isApproved) => {
    setVerificationStatus((prev) => ({
      ...prev,
      [verificationPopup.fieldName]: isApproved,
    }));
    setVerificationPopup({ isOpen: false, fieldName: "", fieldValue: "" });
  };

  const handleChangeWithVerification = (e) => {
    const { name, value } = e.target;

    if (name === "email" || name === "mobile") {
      setVerificationStatus((prev) => ({
        ...prev,
        [name]: false,
      }));

      setVerificationPopup((prev) =>
        prev.isOpen && prev.fieldName === name
          ? { ...prev, fieldValue: value }
          : prev
      );
    }

    handleChange(e);
  };

  const {
    values,
    errors,
    handleSubmit,
    handleChange,
    handleOnBlur,
    initForm,
  } = FormHandler(handleFormUser, (values) => validateFormUser(values, localFormType, isAdvanceOption, selectedRoleId, isTempUserEnable, selectedDate));

  function handleFormUser() {
    setIsLoading(true);
  }

  useEffect(() => {
    setLocalFormType(formType);
  }, [formType]);

  // Handle form submission
  useEffect(() => {
    if (!isLoading) {
      return;
    }
    const expiry = dayjs(selectedDate)
      .hour(23)
      .minute(59)
      .second(59)
      .format("YYYY-MM-DD HH:mm:ss");
    if (localFormType === "add") {
      const createNewUser = async () => {
        try {
          dispatch(toggleLoader(true));

          if (selectedRoleId >= 10) {
            values.references = [values.userreference];
          }

          const metadata = await getMetaData().catch(() => null);
          const hostFromMeta = metadata?.content?.configuration?.configHost;
          const signUpHost = hostFromMeta && /\/sign-up\/?$/.test(hostFromMeta)
            ? hostFromMeta
            : (hostFromMeta ? `${hostFromMeta.replace(/\/$/, '')}/sign-up` : `${window.location.origin}/sign-up`);

          values.host = signUpHost;
          const { status, role, ...filteredValues } = values;
          filteredValues.verifiedFields = verificationStatus;
          if (isTempUserEnable) {
            filteredValues.userExpiryDate = expiry
          }
          const response = await createUser(filteredValues);
          toast.success("User Created Successfully!");
          resetForm(true);
        } catch (error) {
          const status = error?.response?.status;
          const message = error?.response?.data?.message;
          if (status === 422 && message) {
            toast.error(message);
          } else {
            toast.error("Oops... Something went wrong", "Error!");
          }
        } finally {
          setIsLoading(false);
          dispatch(toggleLoader(false));
        }
      };

      createNewUser();
    } else {
      const updateUserDetails = async () => {
        dispatch(toggleLoader(true));
        try {
          // I hope to change this future. because api call object is difference with angular
          let updatedata = {
            lastName: values.lastName,
            email: values.email,
            mobile: values.mobile,
            status: values.status,
            id: selectedUserData.id,
            userRoleId: selectedUserData.userRoleId,
            references: selectedUserData.references,
            lastmodifiedby: selectedUserData.name,
            authTypes: selectedUserData.authTypes,
            verifiedFields: verificationStatus,
          };
          if (isTempUserEnable) {
            updatedata.userExpiryDate = expiry
          }

          if (!updatedata.references || updatedata.references.length === 0) {
            updatedata = { ...updatedata, userRoleId: values.userRoleId };
          } else {
            const { userRoleId, ...updatedDataWithoutUserRole } = updatedata;
            updatedata = { ...updatedDataWithoutUserRole };
          }
          await updateUser(updatedata);
          resetForm(true);
          toast.success("User details updated successfully");
        } catch (error) {
          console.error("Error updating user details:", error);
          const message = error?.response?.data?.message;
          toast.error(message || "Failed to update user details");
        } finally {
          dispatch(toggleLoader(false));
          setIsLoading(false);
        }
      };

      if (isLoading) {
        updateUserDetails();
      }
    }
  }, [isLoading]);

  // Initialize form when the modal is opened
  useEffect(() => {
    initForm({
      lastName: selectedUserData?.lastName || "",
      email: selectedUserData?.email || "",
      mobile: selectedUserData?.mobile || "",
      role: selectedUserData?.role || "",
      status: selectedUserData?.status || "",
      createdByName: (selectedUserData?.createdBy && selectedUserData.createdBy.lastName) || "",
      createdAt: selectedUserData?.creationDate || selectedUserData?.createdAt || "",
      modifiedByName: (selectedUserData?.lastModifiedByUser && selectedUserData.lastModifiedByUser.lastName) || selectedUserData?.lastModifiedBy || "",
      modifiedAt: selectedUserData?.lastModifiedAt || "",
      lastSuccessfulLoginDate: selectedUserData?.lastSuccessfulLoginDate || "",
    });

    setVerificationStatus({
      email: Boolean(selectedUserData?.verifiedFields?.email),
      mobile: Boolean(selectedUserData?.verifiedFields?.mobile),
    });

    if (selectedUserData?.userExpiryDate) {

      const date = selectedUserData.userExpiryDate.split(" ", 1)[0]
      console.log(selectedUserData.userExpiryDate, date)
      setSelectedDate(dayjs(date));
      setIsTempUserEnable(true)
    }
  }, [selectedUserData]);

  //Fetch roles
  useEffect(() => {
    const sortedRoles = Array.isArray(allRoles) ? allRoles.slice().sort((a, b) => a.id - b.id) : [];
    setRoles(sortedRoles);
  }, [allRoles]);

  // Handle role selection change
  const handleRoleChange = (e) => {
    const roleId = e.target.value;
    setSelectedRoleId(roleId);
    handleChange(e);
  };

  // Fetch references based on roleId
  useEffect(() => {
    const fetchReferences = async () => {
      if (!selectedRoleId) return;
      const roleIdNum = Number(selectedRoleId);
      if (roleIdNum >= 10) {
        try {
          dispatch(toggleLoader(true));
          const data = await getReferencesForRole(roleIdNum);
          setReferences(data.content);
        } catch (err) {
          console.error("Error fetching references:", err.message);
          toast.error("Failed to fetch references.");
        } finally {
          dispatch(toggleLoader(false));
        }
      } else {
        setReferences([]);
      }
    };

    fetchReferences();
  }, [selectedRoleId]);

  function resetForm(state) {
    onClose(state);
    initForm({});
    setFormErr("");
    setShowText(false);
    setIsAdvanceOption(false);
  }

  const handleTempUserEnable = (event) => {
    setIsTempUserEnable(event.target.checked);
  }

  const handleExpiredDate = (newDate) => {
    console.log(newDate)
    setSelectedDate(newDate);
    console.log('Selected date:', newDate?.format('YYYY-MM-DD')); // formatted
  };

  useEffect(() => {
    console.log(values)
  }, [values])

  return (
    <div className={"sa-popup-bg "}>
      <div className="sa-popup">
        <form onSubmit={handleSubmit} className={"sa-modal-box-style"}>
          <div className="sa-popup-header">
            <span className={"sa-model-heading"}>{localFormType === "add" ? ("Create User") : localFormType === "edit" ? ("Update User") : ("View User")}</span>
            <div className="sa-popup-close-icon" onClick={() => resetForm(false)}>
              <FeatherIcon className={"sa-modal-close-icon"} icon={"x"} />
            </div>
          </div>
          <div className="sa-popup-content">
            <div className="col-md-6">
              <div className="form-group m-b-16">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  className={`form-control ${errors.lastName && "warning-input"} ${localFormType === 'view' ? 'view-text-muted' : ''}`}
                  onBlur={handleOnBlur}
                  onChange={handleChange}
                  value={values.lastName || ""}
                  name="lastName"
                  id="LastName"
                  placeholder="Enter your Name"
                  disabled={localFormType === 'view'}
                />
                {errors.lastName && (
                  <p className="warning-input-msg">{errors.lastName}</p>
                )}
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group m-b-16">
                <label htmlFor="email">
                  Email
                  <FeatherIcon 
                    icon={"check-circle"} 
                    className="validation-icon"
                    onClick={localFormType === 'edit' ? () => toggleVerification('email') : undefined}
                    style={{
                      cursor: localFormType === 'edit' ? 'pointer' : 'default',
                      color: verificationStatus.email ? 'var(--blue)' : 'var(--gray)'
                    }}
                  />
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.email && "warning-input"} ${localFormType === 'view' ? 'view-text-muted' : ''}`}
                  onBlur={handleOnBlur}
                  onChange={handleChangeWithVerification}
                  value={values.email || ""}
                  name="email"
                  id="email"
                  placeholder="Type your email"
                  disabled={localFormType === 'view'}
                />
                {errors.email && (
                  <p className="warning-input-msg">{errors.email}</p>
                )}
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group m-b-16">
                <label htmlFor="mobile">Mobile</label>
                <FeatherIcon icon={"check-circle"} 
                  className="validation-icon"
                  onClick={localFormType === 'edit' ? () => toggleVerification('mobile') : undefined}
                  style={{ cursor: localFormType === 'edit' ? 'pointer' : 'default', 
                  color: verificationStatus.mobile ? 'var(--blue)' : 'var(--gray)' }}
                />
                <input
                  type="text"
                  className={`form-control ${errors.mobile && "warning-input"} ${localFormType === 'view' ? 'view-text-muted' : ''}`}
                  onBlur={handleOnBlur}
                  onChange={handleChangeWithVerification}
                  value={values.mobile || ""}
                  name="mobile"
                  id="mobile"
                  placeholder="Enter your Mobile"
                  disabled={localFormType === 'view'}
                />
                {errors.mobile && (
                  <p className="warning-input-msg">{errors.mobile}</p>
                )}
              </div>
            </div>
            {localFormType === 'view' && (
              <>
                <div className="col-md-6">
                  <div className="form-group m-b-16">
                    <label htmlFor="role">Type</label>
                    <input
                      type="text"
                      className={`form-control ${localFormType === 'view' ? 'view-text-muted' : ''}`}
                      onBlur={handleOnBlur}
                      onChange={handleChange}
                      value={USER_TYPES[selectedUserData.id] || ""}
                      name="type"
                      id="type"
                      disabled
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group m-b-16">
                    <label htmlFor="role">Role</label>
                    <input
                      type="text"
                      className={`form-control ${localFormType === 'view' ? 'view-text-muted' : ''}`}
                      onBlur={handleOnBlur}
                      onChange={handleChange}
                      value={values.role || "No Role"}
                      name="role"
                      id="role"
                      disabled
                    />
                  </div>
                </div>
                    <div className="col-md-6">
                      <div className="form-group m-b-16">
                        <label htmlFor="createdBy">Created By</label>
                        <input
                          type="text"
                          className={`form-control ${localFormType === 'view' ? 'view-text-muted' : ''}`}
                          value={values.createdByName || ""}
                          name="createdBy"
                          id="createdBy"
                          disabled
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group m-b-16">
                        <label htmlFor="createdAt">Created At</label>
                        <input
                          type="text"
                          className={`form-control ${localFormType === 'view' ? 'view-text-muted' : ''}`}
                          value={values.createdAt || ""}
                          name="createdAt"
                          id="createdAt"
                          disabled
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group m-b-16">
                        <label htmlFor="modifiedBy">Modified By</label>
                        <input
                          type="text"
                          className={`form-control ${localFormType === 'view' ? 'view-text-muted' : ''}`}
                          value={values.modifiedByName || ""}
                          name="modifiedBy"
                          id="modifiedBy"
                          disabled
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group m-b-16">
                        <label htmlFor="modifiedAt">Modified At</label>
                        <input
                          type="text"
                          className={`form-control ${localFormType === 'view' ? 'view-text-muted' : ''}`}
                          value={values.modifiedAt || ""}
                          name="modifiedAt"
                          id="modifiedAt"
                          disabled
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group m-b-16">
                        <label htmlFor="lastSuccessfulLoginDate">Last Successful Login</label>
                        <input
                          type="text"
                          className={`form-control ${localFormType === 'view' ? 'view-text-muted' : ''}`}
                          value={values.lastSuccessfulLoginDate || ""}
                          name="lastSuccessfulLoginDate"
                          id="lastSuccessfulLoginDate"
                          disabled
                        />
                      </div>
                    </div>
              </>
              )
            }
            {localFormType === 'edit' && (
              <div className="col-md-6">
              <div className="form-group m-b-16">
                <label htmlFor="status">{t("title.STATUS")}</label>
                <select
                  className={`form-control ${errors.status && "warning-input"}`}
                  onBlur={handleOnBlur}
                  onChange={handleChange}
                  value={values.status || ""}
                  name="status"
                  id="status"
                  required
                >
                  <option hidden value="">
                    {t("placeHolder.SELECT_YOUR_STATUS")}
                  </option>
                  <option value="ACTIVE">{t("title.ACTIVE")}</option>
                  <option value="INACTIVE">{t("title.INACTIVE")}</option>
                  <option value="SUSPENDED">{t("title.SUSPEND")}</option>
                  <option value="EXPIRED">{t("title.EXPIRED")}</option>
                </select>
                {errors.status && <p className="warning-input-msg">{errors.status}</p>}
              </div>
              </div>
              )
            }
            {localFormType === 'add' && (
              <>
              <div className="col-md-6">
                  <div className="form-group m-b-16">
                    <label htmlFor="role">Role</label>
                    <select
                      className={`form-control ${errors.userRoleId && "warning-input"}`}
                      onBlur={handleOnBlur}
                      onChange={handleRoleChange}
                      value={values.userRoleId || ""}
                      name="userRoleId"
                      id="role"
                    >
                      <option value="">Select a role</option>
                      {roles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.name} ({role.id})
                        </option>
                      ))}
                    </select>
                    {errors.userRoleId && (
                      <p className="warning-input-msg">{errors.userRoleId}</p>
                    )}
                  </div>
                </div>

                {selectedRoleId && Number(selectedRoleId) >= 10 && (
                  <div className="col-md-6">
                    <div className="form-group m-b-16">
                      <label htmlFor="reference">User Reference</label>
                      <select
                        className={`form-control ${errors.userreference && "warning-input"}`}
                        onBlur={handleOnBlur}
                        onChange={handleChange}
                        value={values.userreference || ""}
                        name="userreference"
                        id="reference"
                      >
                        <option value="">Select a reference</option>
                        {references.map((reference) => (
                          <option key={reference.id} value={reference.id}>
                            {reference.name}
                          </option>
                        ))}
                      </select>
                      {errors.userreference && (
                        <p className="warning-input-msg">{errors.userreference}</p>
                      )}
                    </div>
                  </div>
                )}
                </>
            )}
            <div className="col-md-6">
                  <div className="form-group m-b-16">
                    <label className="p-r-12" htmlFor="role">Temporary User</label>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isTempUserEnable}
                          onChange={(event) => handleTempUserEnable(event)}
                          disabled={localFormType === 'view'}
                        />
                      }
                      onClick={handleTempUserEnable}
                    />
                    {isTempUserEnable && <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DemoContainer components={['DatePicker']}>
                        <DatePicker
                          label="Expired Date"
                          value={selectedDate}
                          minDate={dayjs()}
                          onChange={(newValue) => handleExpiredDate(newValue)}
                        />
                      </DemoContainer>
                    </LocalizationProvider>}
                    {errors.tempUser && (
                      <p className="warning-input-msg">{errors.tempUser}</p>
                    )}
                  </div>
                </div>
            {localFormType === 'add' && (
              <>
                <div className="col-md-12">
                  <div className="form-group m-b-16" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontWeight: 500 }}>Advance option</span>
                    <div style={{ flex: 1, borderBottom: '1px solid #eee' }} />
                    <span style={{ cursor: 'pointer', display: 'inline-flex' }} onClick={() => setIsAdvanceOption(!isAdvanceOption)}>
                      <FeatherIcon className="feather-dropdown" icon={isAdvanceOption ? "chevron-up" : "chevron-down"} />
                    </span>
                  </div>
                </div>

                {isAdvanceOption && (
                  <>
                <div className="col-md-6">
                  <div className="form-group m-b-16">
                    <label htmlFor="username">Username</label>
                    <input
                      type="text"
                      className={`form-control ${errors.username && "warning-input"}`}
                      onBlur={handleOnBlur}
                      onChange={handleChange}
                      value={values.username || ""}
                      name="username"
                      id="username"
                      placeholder="Enter your Username"
                    />
                    {errors.username && (
                      <p className="warning-input-msg">{errors.username}</p>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group m-b-16">
                    <label htmlFor="password">New Password</label>
                    <input
                      type={!showText ? "password" : "text"}
                      className={`form-control ${errors.password && "warning-input"}`}
                      onBlur={handleOnBlur}
                      onChange={handleChange}
                      value={values.password || ""}
                      name="password"
                      id="password"
                      placeholder="Enter your new password"
                    />
                    <span className="field-icon" onClick={() => setShowText(!showText)}>
                      <FeatherIcon className="toggle-password" icon={showText ? "eye-off" : "eye"} />
                    </span>
                    {errors.password && (
                      <p className="warning-input-msg">{errors.password}</p>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group m-b-16">
                    <label htmlFor="confirmPassword">Confirm New Password</label>
                    <input
                      type={!showText ? "password" : "text"}
                      className={`form-control ${errors.confirmPassword && "warning-input"}`}
                      onBlur={handleOnBlur}
                      onChange={handleChange}
                      value={values.confirmPassword || ""}
                      name="confirmPassword"
                      id="confirmPassword"
                      placeholder="Retype your new password"
                    />
                    <span className="field-icon" onClick={() => setShowText(!showText)}>
                      <FeatherIcon className="toggle-password" icon={showText ? "eye-off" : "eye"} />
                    </span>
                    {errors.confirmPassword && (
                      <p className="warning-input-msg">{errors.confirmPassword}</p>
                    )}
                  </div>
                </div>
                  </>
                )}
              </>
            )}
          </div>
          {formErr && <div className={"login-warning-msg"}>{formErr}</div>}
          <div className="sa-popup-btn">
            {localFormType !== "view" && (
              <>
                <button
                  className="sa-popup-secondary-btn-style sa-popup-cancel-btn-style"
                  type="button"
                  onClick={() => resetForm(false)}
                >
                  {t("button.CANCEL")}
                </button>
                {localFormType === "add" ? (
                  <button type="submit" className="sa-popup-secondary-btn-style">
                    {t("button.CREATE")}
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="sa-popup-secondary-btn-style sa-popup-edit-btn-style"
                    disabled={values.protected || false}
                  >
                    {t("button.CHANGE")}
                  </button>
                )}
              </>
            )}
            {localFormType === "view" && (
              <button
              className={`sa-popup-secondary-btn-style ${selectedUserData.protect ? 'disable-btn-secondary' : ''}`}
              type="button"
              onClick={() => {
                if (!(selectedUserData.protect)) {
                  setLocalFormType("edit");
                }
              }}
            >
              {t("button.EDIT")}
            </button>
            )}
          </div>
        </form>
      </div>
      {verificationPopup.isOpen && (
        <div className="sa-popup-bg" style={{ zIndex: 1100 }}>
          <div className="sa-popup">
            <div className="sa-modal-box-style">
              <div className="sa-popup-header">
                <span className="sa-model-heading">
                  Verify {verificationPopup.fieldName === 'email' ? 'Email' : 'Mobile'}
                </span>
                <div 
                  className="sa-popup-close-icon" 
                  onClick={() => setVerificationPopup({ isOpen: false, fieldName: "", fieldValue: "" })}
                >
                  <FeatherIcon className="sa-modal-close-icon" icon="x" />
                </div>
              </div>
              <div className="sa-popup-content">
                <div className="col-md-12">
                  <div className="form-group m-b-16">
                    <label>{verificationPopup.fieldName === 'email' ? 'Email Address' : 'Mobile Number'}</label>
                    <input
                      type="text"
                      className="form-control"
                      style={{ borderRadius: '25px' }}
                      value={verificationPopup.fieldValue || ""}
                      readOnly
                    />
                  </div>
                </div>
              </div>
              <div className="sa-popup-btn">
                <button
                  type="button"
                  className="sa-popup-secondary-btn-style sa-popup-cancel-btn-style"
                  style={{ border: "1px solid var(--gray)", color: "var(--gray)", marginRight: "10px" }}
                  onClick={() => handleVerification(false)}
                >
                  {t("button.NOT_APPROVE")}
                </button>
                <button
                  type="button"
                  className="sa-popup-secondary-btn-style"
                  style={{ backgroundColor: "var(--blue)", borderColor: "var(--blue)" }}
                  onClick={() => handleVerification(true)}
                >
                  {t("button.APPROVE")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserForm;
